import React, { useEffect, useMemo, useRef, useState } from "react";
import { listarUsados } from "./api/catalogo";
import "./UsadosTabla.css";
import { Calendar, Search, FileDown, ChevronLeft, ChevronRight, X } from "lucide-react";
import * as XLSX from "xlsx";

/* ── MultiSelect ───────────────────────────────────────────── */
function MultiSelectDropdown({ options, selected, onChange, placeholder = "Filtrar…" }) {
    const [open, setOpen] = useState(false);
    const [queryText, setQueryText] = useState("");
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, []);

    const filtered = (options || []).filter(o => String(o ?? "").toLowerCase().includes(queryText.toLowerCase()));
    const toggle = (val) => onChange(selected.includes(val) ? selected.filter(v => v !== val) : [...selected, val]);
    const label = selected.length === 0 ? placeholder : selected.length === 1 ? selected[0] : `${selected.length} selec.`;

    return (
        <div className="ms-container" ref={ref}>
            <button type="button" className="ms-trigger" onClick={() => setOpen(o => !o)}>
                {label}<span className={`ms-caret ${open ? "open" : ""}`}>▾</span>
            </button>
            {open && (
                <div className="ms-panel" role="listbox">
                    <div className="ms-toolbar">
                        <input className="ms-search" placeholder="Buscar…" value={queryText} onChange={e => setQueryText(e.target.value)} />
                        <button className="ms-action" type="button" onClick={() => onChange([...filtered])}>Todos</button>
                        <button className="ms-action" type="button" onClick={() => onChange([])}>Limpiar</button>
                    </div>
                    <div className="ms-options">
                        {filtered.length === 0 && <div className="ms-empty">Sin resultados</div>}
                        {filtered.map(opt => (
                            <label key={opt} className="ms-option">
                                <input type="checkbox" className="ms-checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                                <span>{opt}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── Helpers ───────────────────────────────────────────────── */
function moneyCLP(n) {
    if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
    return `$${new Intl.NumberFormat("es-CL").format(Number(n))}`;
}

const normalize = (s = "") =>
    String(s).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();

const toSearchTokens = (text) =>
    normalize(text).split(" ").filter(t => t.length >= 2).slice(0, 10);

function getFuelChipClass(combustible = "") {
    const c = combustible.toLowerCase();
    if (c.includes("electr")) return "usados-fuel-chip usados-fuel-chip--electrico";
    if (c.includes("hibrid") || c.includes("hybrid")) return "usados-fuel-chip usados-fuel-chip--hibrido";
    return "usados-fuel-chip";
}

/* ── Component ─────────────────────────────────────────────── */
function UsadosTabla() {
    const [loading, setLoading] = useState(true);
    const [pageItems, setPageItems] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [hasNextByPage, setHasNextByPage] = useState({});

    const [search, setSearch] = useState("");
    const [marcasFiltro, setMarcasFiltro] = useState([]);
    const [aniosFiltro, setAniosFiltro] = useState([]);
    const [fechaDesde, setFechaDesde] = useState("");
    const [fechaHasta, setFechaHasta] = useState("");
    const [montoMin, setMontoMin] = useState("");
    const [montoMax, setMontoMax] = useState("");

    const searchTokens = useMemo(() => toSearchTokens(search), [search]);
    const searchTokensKey = useMemo(() => searchTokens.join("|"), [searchTokens]);
    const aniosFiltroKey = useMemo(() => aniosFiltro.slice().sort().join("|"), [aniosFiltro]);

    const parseMonto = (s) => {
        if (s === "" || s == null) return null;
        const n = Number(String(s).replace(/[^\d]/g, ""));
        return Number.isFinite(n) ? n : null;
    };

    const fetchPage = async (targetPage) => {
        setLoading(true);
        try {
            const { usados: docs, hasMore } = await listarUsados({
                marca: marcasFiltro.length === 1 ? marcasFiltro[0] : undefined,
                tokens: searchTokens.length ? searchTokens : undefined,
                anios: aniosFiltro.length ? aniosFiltro : undefined,
                limit: pageSize,
                offset: (targetPage - 1) * pageSize,
            });
            setPageItems(docs);
            setHasNextByPage(m => ({ ...m, [targetPage]: hasMore }));
            setPage(targetPage);
        } catch (e) {
            console.error("❌ Error cargando usados", e);
            setPageItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setHasNextByPage({});
        fetchPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageSize, searchTokensKey, marcasFiltro.length === 1 ? marcasFiltro[0] : "", aniosFiltroKey]);

    const marcasDisponibles = useMemo(() =>
        Array.from(new Set(pageItems.map(a => a.marca).filter(Boolean))).sort(), [pageItems]);

    const aniosDisponibles = useMemo(() =>
        Array.from(new Set(pageItems.map(a => a.anio).filter(Boolean))).sort((a, b) => b - a), [pageItems]);

    const filteredPageItems = useMemo(() => {
        const min = parseMonto(montoMin), max = parseMonto(montoMax);
        return pageItems.filter(a => {
            const okMarca = !marcasFiltro.length || marcasFiltro.includes(a.marca);
            const okAnio = !aniosFiltro.length || aniosFiltro.includes(a.anio);
            const d = String(a.date_add ?? "");
            const okDesde = !fechaDesde || (d && d >= fechaDesde);
            const okHasta = !fechaHasta || (d && d <= fechaHasta);
            const p = Number(a.precio ?? NaN);
            const okMin = min === null || (!Number.isNaN(p) && p >= min);
            const okMax = max === null || (!Number.isNaN(p) && p <= max);
            return okMarca && okAnio && okDesde && okHasta && okMin && okMax;
        });
    }, [pageItems, marcasFiltro, aniosFiltro, fechaDesde, fechaHasta, montoMin, montoMax]);

    const canPrev = page > 1;
    const canNext = !!hasNextByPage[page];

    const clearFilters = () => {
        setSearch(""); setMarcasFiltro([]); setAniosFiltro([]); setFechaDesde(""); setFechaHasta(""); setMontoMin(""); setMontoMax("");
    };

    const hasFilters = search || marcasFiltro.length || aniosFiltro.length || fechaDesde || fechaHasta || montoMin || montoMax;

    const exportToExcel = () => {
        const data = filteredPageItems.map(r => ({
            Marca: r.marca ?? "", Modelo: r.model ?? "", Detalle: r.modelDetail ?? "",
            Año: r.anio ?? "", Precio: r.precio ?? "", Kilometraje: r.kilometraje ?? "",
            Combustible: r.combustible ?? "", Transmisión: r.transmision ?? "",
            Color: r.color ?? "", Fecha: r.date_add ?? "", Fuente: r.fuente ?? "", carID: r.carID ?? "",
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        ws["!cols"] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 18) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Usados");
        XLSX.writeFile(wb, `usados_pagina_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    return (
        <div className="usados-main">

            {/* Header */}
            <div className="usados-header">
                <div className="usados-header-left">
                    <img src="/logo_doctorcar.png" alt="Logo" className="usados-header-logo" />
                    <div>
                        <h2 className="usados-header-title">Autos usados</h2>
                        <p className="usados-header-meta">
                            Página {page} · {filteredPageItems.length} resultados visibles
                        </p>
                    </div>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                    {hasFilters && (
                        <button className="ut-btn" onClick={clearFilters}>
                            <X size={13} /> Limpiar filtros
                        </button>
                    )}
                    <button className="ut-btn ut-btn--primary" onClick={exportToExcel} disabled={loading || !filteredPageItems.length}>
                        <FileDown size={14} /> Exportar
                    </button>
                </div>
            </div>

            {/* Toolbar */}
            <div className="usados-toolbar">
                {/* Búsqueda */}
                <div className="usados-search">
                    <span className="usados-search-icon"><Search size={14} /></span>
                    <input
                        type="text"
                        placeholder="Buscar modelo (ej: rav4 hybrid awd)…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                {/* Marca */}
                <MultiSelectDropdown
                    options={marcasDisponibles}
                    selected={marcasFiltro}
                    onChange={setMarcasFiltro}
                    placeholder="Marca"
                />

                {/* Año */}
                <MultiSelectDropdown
                    options={aniosDisponibles}
                    selected={aniosFiltro}
                    onChange={setAniosFiltro}
                    placeholder="Año"
                />

                <div className="usados-toolbar-divider" />

                {/* Fechas */}
                <div className="usados-date-group">
                    <Calendar size={13} style={{ color: "#9ca3af" }} />
                    <label>Desde</label>
                    <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
                    <span className="usados-date-sep">→</span>
                    <label>Hasta</label>
                    <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
                </div>

                <div className="usados-toolbar-divider" />

                {/* Montos */}
                <input
                    className="usados-monto-input"
                    inputMode="numeric"
                    value={montoMin}
                    onChange={e => setMontoMin(e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="Precio mín"
                />
                <input
                    className="usados-monto-input"
                    inputMode="numeric"
                    value={montoMax}
                    onChange={e => setMontoMax(e.target.value.replace(/[^\d]/g, ""))}
                    placeholder="Precio máx"
                />
            </div>

            {/* Summary + paginación superior */}
            <div className="usados-summary">
                <div className="usados-summary-text">
                    <span>Mostrando <strong>{filteredPageItems.length}</strong> de <strong>{pageItems.length}</strong> cargados</span>
                    {searchTokens.length > 0 && (
                        <span className="usados-token-chip">
                            🔍 {searchTokens.join(", ")}
                        </span>
                    )}
                    {marcasFiltro.length === 1 && (
                        <span className="usados-marca-chip">
                            🏷 {marcasFiltro[0]}
                        </span>
                    )}
                    {aniosFiltro.length > 0 && (
                        <span className="usados-marca-chip">
                            📅 {aniosFiltro.slice().sort((a, b) => b - a).join(", ")}
                        </span>
                    )}
                </div>
                <div className="usados-summary-right">
                    <select className="usados-page-select" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
                        {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n} / página</option>)}
                    </select>
                    <button className="ut-btn ut-btn--ghost" disabled={!canPrev || loading} onClick={() => fetchPage(page - 1)}>
                        <ChevronLeft size={14} />
                    </button>
                    <span style={{ fontSize: 13, color: "#6b7280", padding: "0 4px" }}>
                        Pág. <strong>{page}</strong>
                    </span>
                    <button className="ut-btn ut-btn--ghost" disabled={!canNext || loading} onClick={() => fetchPage(page + 1)}>
                        <ChevronRight size={14} />
                    </button>
                </div>
            </div>

            {/* Tabla */}
            {loading ? (
                <div className="usados-state">
                    <p>Cargando autos usados…</p>
                </div>
            ) : filteredPageItems.length === 0 ? (
                <div className="usados-state">
                    <Search size={28} style={{ opacity: 0.2 }} />
                    <p>No se encontraron resultados en esta página.</p>
                </div>
            ) : (
                <div className="usados-table-wrap">
                    <table className="usados-table">
                        <thead>
                            <tr>
                                <th>Marca</th>
                                <th>Modelo</th>
                                <th>Versión</th>
                                <th>Año</th>
                                <th className="num">Precio</th>
                                <th className="num">KM</th>
                                <th>Combustible</th>
                                <th>Trans.</th>
                                <th>Color</th>
                                <th>Fecha</th>
                                <th style={{ width: 52, textAlign: "center" }}>Ver</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPageItems.map(a => (
                                <tr key={a.id}>
                                    <td>
                                        {a.marca
                                            ? <span className="usados-marca-badge">{a.marca}</span>
                                            : <span style={{ color: "#d1d5db" }}>—</span>
                                        }
                                    </td>
                                    <td style={{ fontWeight: 500, color: "#111827" }}>{a.model || "—"}</td>
                                    <td style={{ color: "#6b7280", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                        title={a.modelDetail}>{a.modelDetail || "—"}</td>
                                    <td>
                                        {a.anio
                                            ? <span className="usados-anio">{a.anio}</span>
                                            : <span style={{ color: "#d1d5db" }}>—</span>
                                        }
                                    </td>
                                    <td className="num">
                                        <span className="usados-precio">{moneyCLP(a.precio)}</span>
                                    </td>
                                    <td className="num">
                                        <span className="usados-km">{a.kilometraje ? new Intl.NumberFormat("es-CL").format(a.kilometraje) + " km" : "—"}</span>
                                    </td>
                                    <td>
                                        {a.combustible
                                            ? <span className={getFuelChipClass(a.combustible)}>{a.combustible}</span>
                                            : <span style={{ color: "#d1d5db" }}>—</span>
                                        }
                                    </td>
                                    <td style={{ color: "#6b7280" }}>{a.transmision || "—"}</td>
                                    <td style={{ color: "#6b7280" }}>{a.color || "—"}</td>
                                    <td style={{ color: "#9ca3af", fontSize: 12 }}>{a.date_add || "—"}</td>
                                    <td style={{ textAlign: "center" }}>
                                        {a.fuente ? (
                                            <a
                                                href={a.fuente}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="usados-link-btn"
                                                title="Ver publicación"
                                            >
                                                <Search size={13} strokeWidth={2} />
                                            </a>
                                        ) : (
                                            <span style={{ color: "#d1d5db" }}>—</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Footer paginación */}
            {!loading && filteredPageItems.length > 0 && (
                <div className="usados-footer">
                    <span className="usados-footer-text">
                        Página <strong>{page}</strong>
                    </span>
                    <div className="usados-pagination">
                        <button className="ut-btn" disabled={!canPrev || loading} onClick={() => fetchPage(page - 1)}>
                            <ChevronLeft size={14} /> Anterior
                        </button>
                        <button className="ut-btn ut-btn--primary" disabled={!canNext || loading} onClick={() => fetchPage(page + 1)}>
                            Siguiente <ChevronRight size={14} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default UsadosTabla;